import { AfterViewChecked, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SnackbarComponent } from 'app/snackbar/snackbar.component';
import { ApiService } from 'app/ts/ApiService';
import { DocumentUploadList } from 'app/ts/models/broker-interfaces';
import { LoanApplication } from 'app/ts/models/loan-classes';
import { ClientStoreService } from 'app/ts/services/client-store.service';
import { IdentificationTypeService } from 'app/ts/services/identification-type.service';
import { ProductSelectService } from 'app/ts/services/product-select.service';
import { environment } from 'environments/environment';
import { FileUploader } from 'ng2-file-upload';

@Component({
  selector: 'app-upload-documents',
  templateUrl: './upload-documents.component.html',
  styleUrls: ['./upload-documents.component.scss', '../../forms.shared.scss']
})
export class UploadDocumentsComponent implements OnInit {
  @Output() updateStepEvent = new EventEmitter<number>();
  @Output() previousStepEvent = new EventEmitter<number>();


  loanApplication$ = this._productSelect.loanApplication;
  isPassport$ = this._identityType.isPassport;

  uploader: FileUploader;
  hasBaseDropZoneOver = false;
  baseUrl = environment.apiURL;
  maximumFileSize = 40 * 1024 * 1024; // File size in MB

  selectedDocument: string;
  selectedChildDocument: string;
  selectedDocumentDescription: string;
  documents: DocumentUploadList[];
  form: FormGroup;
  loanType: string;
  clientId: number = 0;
  staffId: number = 0;
  client: any;
  isSectionLocked = false;
  canFinish:boolean = false;

  constructor(private api: ApiService,
    private snackbar: MatSnackBar,
    public formBuilder: FormBuilder,
    public _productSelect: ProductSelectService,
    private clientStore: ClientStoreService,
    public _identityType: IdentificationTypeService) {
    
  }

  setDocumentList() {
    this.documents = [
      { key: 'id', description: 'Id Copy', isUploaded: false },
      { key: 'bankstatement', description: 'Latest 3 Months Bank Statements', isUploaded: false },
      { key: "payslip", description: 'Latest 3 Months Payslip', isUploaded: false },
      { key: 'applicationform', description: 'Loan Application Form', isUploaded: false },
      { key: 'hrConfirmationForm', description: 'HR confirmation Form', isUploaded: false },
      { key: 'powerOfAttorney', description: 'Power of Attorney Form', isUploaded: false },
      {
        key: 'settlementletters',
        description: 'Settlement Letters',
        isUploaded: false,
        expanded: false,
        children: [
          { id: 1, key: 'settlementletters', description: 'Settlement Letter (01)', isUploaded: false },
        ]
      },
    ];

  }


  addSettlementLetter() {
    const parent = this.documents.find(d => d.key === 'settlementletters');
    const nextIndex = parent.children.length + 1;

    if (nextIndex > 10) {
      this.openSnackBar('You can only upload a maximum of 10 settlement letters.', 'error-snackbar');
      return;
    }

    parent.children.push({
      id: nextIndex,
      key: 'settlementletters',
      description: `Settlement Letter (${nextIndex.toString().padStart(2, '0')})`,
      isUploaded: false
    });
  }

  removeSettlementLetter(index: number) {
    const parent = this.documents.find(d => d.key === 'settlementletters');
    parent.children.splice(index, 1);

    // Re-number
    parent.children = parent.children.map((c, i) => ({
      ...c,
      id: i + 1,
      description: `Settlement Letter (${(i + 1).toString().padStart(2, '0')})`
    }));
  }


  ngOnInit(): void {
    this.clientStore.client$.subscribe(client => {     // async updates
      this.client = client;
    });

    this.setDocumentList();

  }

  initializeUploader() {
    this.clientId =  this.client ? this.client.clientId : 0;//Number(sessionStorage.getItem('clientId'));
    this.staffId = this.client ? this.client.staffId : 0;//Number(sessionStorage.getItem("staffId"));

    if (this.clientId == 0 || this.staffId == 0) {
      // this.openSnackBar('An error occurred. Please try again.', "error-snackbar");
      return;
    }

    const doc = this.getCurrentDocument();

    this.uploader = new FileUploader({
      url: `${this.baseUrl}Portal/upload-client-documents/${Number(this.clientId)}/${Number(this.staffId)}/${this.selectedDocument}`,
      isHTML5: true,
      allowedFileType: ['pdf', 'image'],
      removeAfterUpload: false,
      autoUpload: false,
      maxFileSize: this.maximumFileSize,
    });

    if (doc.uploadedFile) {
      const fakeFile = new File([doc.uploadedFile], doc.uploadedFile.name);
      this.uploader.addToQueue([fakeFile]);
      this.uploader.progress = 100;
      this.uploader.queue[0].isSuccess = true;
      this.uploader.queue[0].isUploaded = true;
      this.uploader.options.queueLimit = 1;
    }

    this.uploader.onProgressItem = (file) => {
      let index = this.uploader.queue.indexOf(file);
      this.uploader.queue[index].progress = 0;
      this.uploadFilesSimulator(index, this.uploader.queue);
    }

    this.uploader.onSuccessItem = (item, response, status, headers) => {
      if (status = 204) {
        this.openSnackBar(`${this.selectedDocumentDescription} Successfully uploaded.`, "success-snackbar");
        let updateItem = this.documents.find(this.findIndexToUpdate, this.selectedDocument);
        let index = this.documents.indexOf(updateItem);

        if (this.selectedDocument.startsWith('settlement-')) {
          const settlement = this.documents.find(d => d.key === 'settlementletters');
          const id = Number(this.selectedDocument.replace('settlement-', ''));
          const child = settlement.children.find(c => c.id === id);
          const childIndex = settlement.children.indexOf(child);
          settlement.children[childIndex].isUploaded = true;
          settlement.children[childIndex].uploadedFile = item._file;
        } else {
          this.documents[index].isUploaded = true;
          this.documents[index].uploadedFile = item._file;
        }
      }
    }

    this.uploader.onAfterAddingFile = (fileItem) => {
      if (this.uploader.queue.length > 1 && !this.uploader.queue[0].isUploaded) {
        // Remove the old file
        const oldFile = this.uploader.queue[0];
        this.uploader.removeFromQueue(oldFile);
      }
      if (this.uploader.queue.length > 0 && this.uploader.queue[0].isUploaded) {
        this.isSectionLocked = true;
        fileItem.remove();
        this.openSnackBar(`You have already uploaded a file for ${this.selectedDocumentDescription}.`, 'error-snackbar');
      }
    };

    this.uploader.onErrorItem = (item, response, status, headers) => {
      let message = `Could not upload the selected ${this.selectedDocumentDescription}`;

      if (response) {
        message = `${message}, ${response}`;
      }

      this.openSnackBar(message, "error-snackbar");
    }

    // this.uploader.response.subscribe(i => this.response)

  }


  getCurrentDocument() {
    if (this.selectedDocument.startsWith('settlement-')) {
      const settlement = this.documents.find(d => d.key === 'settlementletters');
      const id = Number(this.selectedDocument.replace('settlement-', ''));
      return settlement.children.find(c => c.id === id);
    }
    return this.documents.find(d => d.key === this.selectedDocument);
  }

  selectDocument(key: string) {
    if (this.hasPendingFiles()) {
      this.confirmDiscardPendingFiles(key);
      return;
    }

    this.selectedDocument = key;

    const settlement = this.documents.find(d => d.key === 'settlementletters');

    settlement.expanded = false;

    if (key === 'settlementletters') {
      // toggle expand/collapse
      settlement.expanded = !settlement.expanded;
    }

    // if child selected
    if (key.startsWith('settlement-') || key === 'settlementletters') {
      settlement.expanded = true;

      let id;

      if(key === 'settlementletters') {
        id = 1;
      } else {
        id = Number(key.replace('settlement-', ''));
      }
      const child = settlement.children.find(c => c.id === id);
      this.selectedDocument = `settlement-${id}`;
      this.selectedDocumentDescription = child.description;
    } else {
      const doc = this.documents.find(d => d.key === key);
      this.selectedDocumentDescription = doc.description;
    }

    this.initializeUploader();
  }

  hasPendingFiles(): boolean {
    return this.uploader && this.uploader.queue.length > 0 && !this.uploader.queue[0].isUploaded;
  }

  confirmDiscardPendingFiles(nextKey: string) {
    const confirmResult = confirm(
        "You have a file waiting to be uploaded. If you continue, the file will be discarded.\n\nDo you want to continue?"
      );

      if (confirmResult) {
        // user accepts → clear queue and continue
        this.uploader.clearQueue();
        this.selectDocument(nextKey);
      }

      // else → do nothing, stay on the same section
  }

  fileOverBase(e: any) {
    this.hasBaseDropZoneOver = e;
  }

  findIndexToUpdate(newItem) {
    return newItem.key === this;
  }

  /**
   * Simulate the upload process
   */
  uploadFilesSimulator(index: number, fileArray: any[]) {

    setTimeout(() => {
      if (index === fileArray.length) {
        return;
      } else {
        const progressInterval = setInterval(() => {
          if (fileArray[index].progress === 100) {
            clearInterval(progressInterval);
            this.uploadFilesSimulator(index + 1, fileArray);
          } else {
            fileArray[index].progress += 5;
          }
        }, 50);
      }
    }, 1000);
  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
      return "0 Bytes";
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  finish(): void {
    const missingDocs: string[] = [];

    this.documents.forEach(doc => {
      if (doc.children) {
        // Check children
        doc.children.forEach(child => {
          if (!child.isUploaded) {
            missingDocs.push(child.description);
          }
        });

        // Optional: if parent doc itself is treated separately
        if (!doc.isUploaded && doc.key !== 'settlementletters') {
          missingDocs.push(doc.description);
        }

      } else {
        // Normal documents
        if (!doc.isUploaded && doc.key !== 'powerOfAttorney' && doc.key !== 'hrConfirmationForm') { 
          missingDocs.push(doc.description);
        }
      }
    });

    // If missing docs exist, stop and show the list
    if (missingDocs.length > 0) {
      this.openSnackBar(
        `Please upload the following documents: ${missingDocs.join(', ')}`,
        'error-snackbar'
      );
      return;
    }

    // Otherwise proceed
    this.api.sendUlEmail(Number(this.client.staffId), Number(this.client.clientId))
      .subscribe(() => {
        this.openSnackBar(
          'Congratulations you have successfully submitted your application please proceed with your next customer',
          'success-snackbar'
        );
      });

    this.updateStepEvent.emit();
  }

  openSnackBar(msg: string, panel: string) {
    this.snackbar.openFromComponent(SnackbarComponent, {
      data: msg,
      panelClass: [panel],
      duration: 8000,
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }
}
